import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { FUENTE, useTema } from './tema';

/**
 * LienzoDeFirma — dibujar la rúbrica con el ratón o el dedo.
 *
 * Lo que sale de aquí acaba estampado en la celda «Firma» de la fila de esa
 * persona, en la tabla de asistentes del acta. Por eso el lienzo es apaisado y
 * no cuadrado: tiene la proporción del hueco donde va a terminar, y así lo que
 * se dibuja se parece a lo que se imprime.
 *
 * ── Tres cosas que no son de estilo ─────────────────────────────────────
 *
 * **El fondo es transparente, no blanco.** La firma cae dentro de una celda que
 * puede tener sombreado o bordes, y un rectángulo blanco encima se ve como un
 * parche pegado. El PNG sale con alfa y solo lleva el trazo.
 *
 * **Se dibuja con punteros, no con ratón.** `pointerdown`/`pointermove` cubre
 * ratón, dedo y lápiz con un solo camino; con eventos de ratón, firmar desde el
 * móvil —que es desde donde se abre un correo— no funcionaría, y con eventos
 * táctiles aparte habría dos caminos que mantener sincronizados.
 *
 * **El lienzo tiene más píxeles que tamaño.** Se dibuja a la resolución real de
 * la pantalla (`devicePixelRatio`): en un portátil moderno, un lienzo de 1:1 da
 * un trazo con los bordes dentados, y esa firma dentada es la que queda impresa
 * en el acta para siempre.
 */

/** Lo ancho del lienzo en relación a su alto. El del hueco donde acaba. */
const PROPORCION = 3.2;

/** El grosor del trazo, en píxeles de CSS. Una firma fina parece un arañazo. */
const GROSOR = 2.4;

const estilos = (C) => ({
  caja: { marginTop: 4 },

  marco: (hayTrazo) => ({
    position: 'relative',
    border: `1px ${hayTrazo ? 'solid' : 'dashed'} ${hayTrazo ? C.borde : C.suave}`,
    borderRadius: 10,
    background: C.panel,
    overflow: 'hidden',
    // Que el navegador no interprete el arrastre del dedo como desplazar la
    // página: sin esto, firmar en el móvil mueve la pantalla y no pinta nada.
    touchAction: 'none',
    cursor: 'crosshair'
  }),

  lienzo: { display: 'block', width: '100%', height: '100%' },

  pista: {
    position: 'absolute', inset: 0, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    color: C.tenue, fontSize: 13.5, pointerEvents: 'none', textAlign: 'center',
    padding: '0 16px'
  },

  // La raya sobre la que se firma, como en el papel. Ayuda a centrar el trazo y
  // deja claro que ahí se escribe.
  raya: {
    position: 'absolute', left: '8%', right: '8%', bottom: '26%',
    height: 1, background: C.borde, pointerEvents: 'none'
  },

  pie: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 7 },

  borrar: {
    padding: '5px 11px', borderRadius: 7, fontSize: 12.5, fontFamily: FUENTE,
    background: 'transparent', color: C.suave, border: `1px solid ${C.borde}`,
    cursor: 'pointer'
  },

  nota: { fontSize: 12, color: C.tenue, lineHeight: 1.5 }
});

/**
 * @param {Object} props
 * @param {Function} props.onCambio - recibe el PNG en base64 (sin cabecera
 *        `data:`), o null cuando el lienzo se queda vacío.
 * @param {string} props.nombre - el de quien firma, para la pista
 * @param {boolean} props.desactivado
 */
const LienzoDeFirma = ({ onCambio, nombre = null, desactivado = false }) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const lienzoRef = useRef(null);
  const marcoRef = useRef(null);
  const pintando = useRef(false);
  const ultimo = useRef(null);
  const [hayTrazo, setHayTrazo] = useState(false);

  // ── El lienzo se ajusta a su hueco, y al hacerlo se borra ───────────────
  //
  // Cambiar `width` o `height` de un canvas limpia su contenido: es así por
  // especificación. Así que esto corre una vez al montar y cuando el hueco
  // cambia de tamaño de verdad —girar el móvil—, no en cada pintado de React:
  // si no, el trazo desaparecería mientras se dibuja.
  const ajustar = useCallback(() => {
    const marco = marcoRef.current;
    const lienzo = lienzoRef.current;
    if (!marco || !lienzo) return;

    const ancho = marco.clientWidth;
    const alto = Math.round(ancho / PROPORCION);
    const densidad = window.devicePixelRatio || 1;

    marco.style.height = `${alto}px`;
    lienzo.width = Math.round(ancho * densidad);
    lienzo.height = Math.round(alto * densidad);

    const ctx = lienzo.getContext('2d');
    ctx.scale(densidad, densidad);
    ctx.lineWidth = GROSOR;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Negro siempre, aunque la aplicación esté en modo oscuro. Lo que se ve
    // aquí va a acabar en un papel blanco: una firma blanca sería invisible.
    ctx.strokeStyle = '#111827';
  }, []);

  useEffect(() => {
    ajustar();
    const marco = marcoRef.current;
    if (!marco || typeof ResizeObserver === 'undefined') return undefined;

    let anchoPrevio = marco.clientWidth;
    const vigia = new ResizeObserver(() => {
      // Solo si cambió de verdad. Un ResizeObserver se dispara también por
      // cambios de un píxel al aparecer una barra de desplazamiento, y cada uno
      // borraría la firma a medio hacer.
      if (Math.abs(marco.clientWidth - anchoPrevio) < 2) return;
      anchoPrevio = marco.clientWidth;
      ajustar();
      setHayTrazo(false);
      if (onCambio) onCambio(null);
    });
    vigia.observe(marco);
    return () => vigia.disconnect();
  }, [ajustar, onCambio]);

  const puntoDe = (e) => {
    const r = lienzoRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const empezar = (e) => {
    if (desactivado) return;
    e.preventDefault();
    // Capturar el puntero: así el trazo sigue aunque el dedo salga del lienzo,
    // y al soltar fuera se recibe el `pointerup` igualmente. Sin esto, salirse
    // por el borde deja el lienzo creyendo que todavía se está dibujando.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* da igual */ }
    pintando.current = true;
    ultimo.current = puntoDe(e);
  };

  const mover = (e) => {
    if (!pintando.current || desactivado) return;
    e.preventDefault();
    const ctx = lienzoRef.current.getContext('2d');
    const ahora = puntoDe(e);
    ctx.beginPath();
    ctx.moveTo(ultimo.current.x, ultimo.current.y);
    ctx.lineTo(ahora.x, ahora.y);
    ctx.stroke();
    ultimo.current = ahora;
    if (!hayTrazo) setHayTrazo(true);
  };

  const soltar = useCallback(() => {
    if (!pintando.current) return;
    pintando.current = false;
    ultimo.current = null;
    if (!onCambio) return;
    // El base64 pelado: la cabecera `data:` la vuelve a poner quien la necesite.
    const datos = lienzoRef.current.toDataURL('image/png');
    onCambio(datos.replace(/^data:image\/png;base64,/, ''));
  }, [onCambio]);

  const limpiar = () => {
    const lienzo = lienzoRef.current;
    const ctx = lienzo.getContext('2d');
    // En coordenadas del lienzo, no de CSS: el contexto está escalado por la
    // densidad de pantalla, así que limpiar `clientWidth` dejaría sin borrar la
    // parte derecha en una pantalla de retina.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, lienzo.width, lienzo.height);
    ctx.restore();
    setHayTrazo(false);
    if (onCambio) onCambio(null);
  };

  return (
    <div style={S.caja}>
      <div ref={marcoRef} style={S.marco(hayTrazo)}>
        <canvas
          ref={lienzoRef}
          style={S.lienzo}
          onPointerDown={empezar}
          onPointerMove={mover}
          onPointerUp={soltar}
          onPointerCancel={soltar}
          aria-label={nombre ? `Dibuja aquí tu firma, ${nombre}` : 'Dibuja aquí tu firma'}
        />
        <div style={S.raya} aria-hidden="true" />
        {!hayTrazo && (
          <div style={S.pista}>Dibuja aquí tu firma, con el ratón o con el dedo</div>
        )}
      </div>

      <div style={S.pie}>
        <button type="button" style={S.borrar} onClick={limpiar} disabled={!hayTrazo || desactivado}>
          Borrar y repetir
        </button>
        <span style={S.nota}>
          {/* Hace falta para aprobar. Lo dice aquí, junto al lienzo, y no solo
              al pulsar: enterarse de un requisito por un error es enterarse
              tarde. Antes ponía «es opcional» y el botón la daba por firmada
              sin trazo, que era peor que las dos cosas. */}
          Hace falta para firmar. Aparecerá en tu fila del acta.
        </span>
      </div>
    </div>
  );
};

export default LienzoDeFirma;
